/*
 * yojo-cart-intent.js — the fulfillment-intent toggle (משלוח / איסוף עצמי).
 *
 * Why this is a standalone asset and not the inline <script> it started as
 * (post-live sweep, 2026-08-27, P0): the cart drawer is injected with
 * innerHTML from `?section_id=helper-cart`, and a <script> inserted via
 * innerHTML NEVER executes. The listener therefore registered on exactly zero
 * page loads — `window.yojoIntentToggle` was undefined before and after the
 * drawer rendered, clicking איסוף עצמי issued no /cart/update.js, and the
 * chips repainted nothing. The whole pickup path was dead on arrival for
 * every customer, while every other piece of the feature (the attribute
 * write, refreshCart, the server-rendered chip states) was healthy.
 *
 * Loaded once from layout/theme.liquid with `defer`, so it is a real
 * document-level listener that survives every drawer re-render.
 *
 * The click is delegated and capture-phase because the chips are replaced
 * wholesale on each repaint; `busy` prevents a double-write while the cart
 * mutation is in flight.
 */
(function () {
  if (window.yojoIntentToggle) return;
  window.yojoIntentToggle = true;

  /*
   * T01(a) 2026-09-03 (A3-06/A3-07/A1-15): the delivery-zone gate (city
   * select) and the pickup WhatsApp notice both render as siblings of
   * #AjaxCartSubtotal in <aside> (sections/main-cart.liquid), OUTSIDE the
   * #AjaxCartForm region that window.refreshCart repaints. A chip click on
   * /cart therefore updated the cart attribute and repainted the ladder/gift
   * line, but left the zone gate showing the OLD intent's city question and
   * the pickup notice missing entirely — exactly the "still 137 short, still
   * asking for a city, after choosing pickup" contradiction from the audit.
   * The chips are the one owner of intent now: after every re-render, read
   * the fresh [data-yojo-intent] value (it lives inside #AjaxCartForm via
   * cart-aux.liquid, so it IS fresh) and toggle `hidden` on the two
   * intent-gated blocks server-rendered by main-cart.liquid.
   */
  function syncZoneToIntent() {
    var intentEl = document.querySelector('[data-yojo-intent]');
    var isPickup = !!intentEl && intentEl.getAttribute('data-yojo-intent') === 'pickup';
    var zoneRoot = document.querySelector('[data-yojo-delivery-zone]');
    if (zoneRoot) zoneRoot.hidden = isPickup;
    var pickupNoticeRoot = document.querySelector('[data-yojo-pickup-notice-root]');
    if (pickupNoticeRoot) pickupNoticeRoot.hidden = !isPickup;
  }
  document.addEventListener('yojo:cart:refreshed', syncZoneToIntent);
  document.addEventListener('cart:updated', syncZoneToIntent);

  var busy = false;

  /*
   * 2026-11-04 pickup pause: a cart that still carries
   * fulfillment_intent=pickup from before the pause is rendered as delivery
   * by Liquid (snippets/yojo-fulfillment-intent), but the attribute itself
   * would still travel into the order and tell the packer "pickup". The
   * server marks such a cart ([data-yojo-intent-stale="pickup"],
   * snippets/cart-aux.liquid); this rewrites the attribute to delivery once,
   * quietly (no reload: the render is already correct).
   */
  function healStaleIntent() {
    if (!window.YOJO_PICKUP_PAUSED || busy) return;
    var stale = document.querySelector('[data-yojo-intent-stale="pickup"]');
    if (!stale) return;
    busy = true;
    stale.removeAttribute('data-yojo-intent-stale');
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { fulfillment_intent: 'delivery' } })
    })
      .catch(function () { /* the next cart mutation heals it */ })
      .then(function () { busy = false; });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', healStaleIntent);
  } else {
    healStaleIntent();
  }
  document.addEventListener('yojo:cart:refreshed', healStaleIntent);
  document.addEventListener('cart:updated', healStaleIntent);

  document.addEventListener('click', function (event) {
    var target = event.target;
    var chip = target && target.closest ? target.closest('[data-yojo-intent-chip]') : null;
    if (!chip || busy) return;

    var group = chip.closest('[data-yojo-intent]');
    var next = chip.getAttribute('data-yojo-intent-chip');
    /* 2026-11-04 pickup pause: no element may write pickup while the theme
       says pickup is paused (window.YOJO_PICKUP_PAUSED, from
       snippets/yojo-delivery-zone-runtime.liquid). Belt and braces: the chips
       and both switch buttons are not rendered in that state either. */
    if (next === 'pickup' && window.YOJO_PICKUP_PAUSED) return;
    if (group && group.getAttribute('data-yojo-intent') === next) return;

    busy = true;
    chip.setAttribute('disabled', 'disabled');

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { fulfillment_intent: next } })
    })
      .then(function (response) {
        /* Guard before trusting the body: a Cloudflare challenge or a 5xx
           returns HTML here, and an unguarded .json() throws an unhandled
           rejection into the console instead of failing quietly. */
        if (!response.ok) throw new Error('cart update rejected: ' + response.status);
        if (window.refreshCart) return window.refreshCart({ open: false });
        /* The chips also render on /cart (F4), where the drawer's refresh
           helper does not exist. Reload so the ladder, the gift line and the
           recommendation shelf all repaint from one server render rather than
           leaving the page showing the previous intent. */
        window.location.reload();
        return null;
      })
      .then(function () {
        /* main-cart's free-shipping slot and the context listeners repaint
           off these two events. */
        document.dispatchEvent(new CustomEvent('yojo:cart:refreshed'));
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .catch(function () {
        /* Re-render path failed; the next cart mutation heals the state.
           Release the chip so the control never stays stuck disabled. */
        chip.removeAttribute('disabled');
      })
      .then(function () {
        busy = false;
      });
  }, true);
})();
